import { Link } from '@/components/Elements';
import { Button } from '@/components/Elements/Button';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import {
  PriceDisplay,
  PromotionPrice,
} from '@/features/subscription/components';
import { SubscriptionLayout } from '@/features/subscription/components/SubscriptionLayout';
import { useSubscription } from '@/features/subscription/hooks/useSubscription';
import { usePlusFeatures } from '@/features/subscription/hooks/usePlusFeatures';
import { useUserPermission } from '@/lib/auth';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import RWPlusLogo from '@/assets/rw-plus-logo.png';
import { getUpgradeCopy } from '@/lib/copy/upgrade';

export const Subscription = () => {
  const { t, i18n } = useTranslation();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const campaignCode = searchParams.get('campaign_code')?.trim() || null;
  const displayBasicParam = searchParams.get('display_basic');
  const hideBasicLink = displayBasicParam === 'false';
  const features = usePlusFeatures();
  const defaultTrialDays = 7;

  const defaultCountryCode = i18n.language.startsWith('ja') ? 'jp' : undefined;
  const countryParam = searchParams.get('country')?.trim().toLowerCase();
  const isoCountryCode = countryParam || defaultCountryCode;

  const {
    isYearly,
    setIsYearly,
    uiErrorMessage,
    currentPrice,
    yearlyDiscountPercentage,
    handleSubscribe,
    isMutating,
  } = useSubscription({ campaignCode, isoCountryCode });
  const { data: user } = useUserPermission();
  const canTrial = user ? !user.hasTrialHistory : true;
  const trialPeriodDays = currentPrice?.trialPeriodDays ?? null;
  const hasTrial = canTrial && (trialPeriodDays ?? 0) > 0;
  const hasDiscount =
    currentPrice?.discountType != null &&
    currentPrice.discountValue != null &&
    currentPrice.discountValue > 0;
  const isTrialExtended =
    trialPeriodDays != null && trialPeriodDays > defaultTrialDays;
  const isCampaignApplied = hasDiscount || isTrialExtended;
  const shouldShowTrialOverride =
    campaignCode &&
    hasTrial &&
    trialPeriodDays != null &&
    trialPeriodDays > defaultTrialDays;
  const copy = getUpgradeCopy(t, trialPeriodDays);

  return (
    <SubscriptionLayout>
      <div className="w-full space-y-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <img
            src={RWPlusLogo}
            alt="RuleWatcher Plus"
            className="h-12 w-auto sm:h-[50px]"
            loading="lazy"
            decoding="async"
          />
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-teal">
            {copy.headline}
          </p>
          <p className="text-sm leading-relaxed text-gray-700">{copy.sub}</p>
        </div>

        <div className="rounded-[20px] bg-gradient-to-l from-gradient-teal-start/50 to-gradient-teal-end/50 p-[1.5px]">
          <div className="divide-y divide-gray-100 overflow-hidden rounded-[18px] bg-white p-4 shadow-sm lg:p-5">
            {features.map(({ key, icon, title, desc }) => (
              <div
                key={key}
                className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0"
              >
                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-teal/10 text-primary-teal">
                  {icon}
                </div>
                <div className="space-y-0.5">
                  <p className="text-base font-semibold leading-snug text-gray-900">
                    {title}
                  </p>
                  <p className="text-sm leading-relaxed text-gray-600">
                    {desc}
                  </p>
                  {key === 'csv-download' && hasTrial && (
                    <p className="text-xs text-gray-500">
                      {copy.trialLimitNote}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm">
          <ToggleSwitch
            checked={isYearly}
            onToggle={(checked) => setIsYearly(checked)}
            labels={[t('月払い'), t('年払い')]}
          />

          <PriceDisplay
            price={currentPrice}
            discountPercentage={yearlyDiscountPercentage}
            recurringInterval={isYearly ? 'year' : 'month'}
          />

          <PromotionPrice
            price={currentPrice}
            trialPeriodDays={trialPeriodDays}
            isCampaignApplied={isCampaignApplied}
            hasTrial={hasTrial}
            showTrialDescription={isTrialExtended}
          />
        </div>

        <div className="space-y-4">
          <Button
            onClick={handleSubscribe}
            isLoading={isMutating}
            disabled={isMutating}
            color="dark"
            size="lg"
            noBordered
            className="w-full rounded-full border-0 bg-gradient-to-l from-gradient-teal-start to-gradient-teal-end text-white shadow-none hover:opacity-90"
          >
            {shouldShowTrialOverride ? (
              <span className="flex flex-col items-center leading-tight">
                <span className="text-xs font-medium text-white/80 line-through">
                  {t('{{days}}日間無料で試す', { days: defaultTrialDays })}
                </span>
                <span className="text-lg font-semibold">{copy.ctaTrial}</span>
              </span>
            ) : (
              <span className="text-lg font-semibold">
                {hasTrial ? copy.ctaTrial : copy.ctaUpgrade}
              </span>
            )}
          </Button>
          {!hideBasicLink && (
            <Link
              to="/themes"
              className="block w-full text-center text-sm"
              color="secondary"
              underline="hover"
            >
              {copy.basicCta}
            </Link>
          )}
          {uiErrorMessage && (
            <p className="mt-2 text-sm text-red-600">{uiErrorMessage}</p>
          )}
        </div>
      </div>
    </SubscriptionLayout>
  );
};
